import { useMemo, useCallback } from 'react';
import type { ChatWidgetProps, ChatWidgetSettings } from '../types';
import { DEFAULT_THEME } from '../constants';
import { useEndpointConfig } from './useEndpointConfig';
import { useWidgetBehavior } from './useWidgetBehavior';

function mergeSettings(
  base?: ChatWidgetSettings | null,
  override?: ChatWidgetSettings | null
): ChatWidgetSettings {
  if (!base && !override) return {};
  if (!base) return override || {};
  if (!override) return base;
  const result: Record<string, unknown> = { ...base };
  Object.entries(override).forEach(([key, value]) => {
    const baseValue = (base as Record<string, unknown>)[key];
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      baseValue &&
      typeof baseValue === 'object' &&
      !Array.isArray(baseValue)
    ) {
      result[key] = mergeSettings(baseValue as ChatWidgetSettings, value as ChatWidgetSettings);
    } else {
      result[key] = value;
    }
  });
  return result as ChatWidgetSettings;
}

export function useResolvedProps(
  props: ChatWidgetProps,
  remoteSettings: ChatWidgetSettings | null
) {
  const settings = useMemo(
    () => mergeSettings(remoteSettings, props.settings),
    [remoteSettings, props.settings]
  );

  const readSetting = useCallback(
    (path: string) =>
      path
        .split('.')
        .reduce(
          (acc: Record<string, unknown>, key) =>
            (acc && (acc as Record<string, unknown>)[key] !== undefined
              ? (acc as Record<string, unknown>)[key]
              : undefined) as Record<string, unknown>,
          settings as Record<string, unknown>
        ),
    [settings]
  );

  const resolveProp = useCallback(
    <T>(prop: T | undefined, paths: string[], fallback: T): T => {
      if (prop !== undefined) return prop;
      for (const p of paths) {
        const value = readSetting(p);
        if (value !== undefined) return value as T;
      }
      return fallback;
    },
    [readSetting]
  );

  // Branding
  const companyName = resolveProp(
    props.companyName,
    ['branding.companyName', 'companyName'],
    'Acme Inc.'
  );
  const companyLogo = resolveProp(props.companyLogo, ['branding.companyLogo', 'companyLogo'], '');
  const agentName = resolveProp(
    props.agentName,
    ['branding.agentName', 'agentName'],
    'AI Assistant'
  );
  const agentAvatar = resolveProp(props.agentAvatar, ['branding.agentAvatar', 'agentAvatar'], '');
  const theme = resolveProp(props.theme, ['theme', 'branding.theme'], DEFAULT_THEME);
  const darkMode = props.darkMode || 'light';
  const welcomeMessage = resolveProp(
    props.welcomeMessage,
    ['branding.welcomeMessage', 'welcomeMessage'],
    ''
  );
  const suggestedActions = resolveProp(
    props.suggestedActions,
    ['branding.suggestedActions', 'suggestedActions'],
    []
  );
  const bubbleOptions = resolveProp(
    props.bubbleOptions,
    ['branding.bubbleOptions', 'bubbleOptions'],
    []
  );
  const placeholder = resolveProp(
    props.placeholder,
    ['branding.placeholder', 'placeholder'],
    'Type a message...'
  );
  const language = resolveProp(props.language, ['branding.language', 'language'], 'en');

  // Agent config
  const agentOrgId = resolveProp(props.orgId ?? props.agent?.orgId, ['agent.orgId', 'orgId'], '');
  const agentId = resolveProp(
    props.agentId ?? props.agent?.agentId,
    ['agent.agentId', 'agentId'],
    ''
  );
  const kbCollection = resolveProp(
    props.kbCollection ?? props.agent?.kbCollection,
    ['agent.kbCollection', 'kbCollection'],
    ''
  );
  const kbApiKey = resolveProp(
    props.kbApiKey ?? props.agent?.kbApiKey,
    ['agent.kbApiKey', 'kbApiKey'],
    ''
  );
  const kbTopK = resolveProp<number | undefined>(
    props.kbTopK ?? props.agent?.kbTopK,
    ['agent.kbTopK', 'kbTopK'],
    undefined
  );
  const kbScoreThreshold = resolveProp<number | undefined>(
    props.kbScoreThreshold ?? props.agent?.kbScoreThreshold,
    ['agent.kbScoreThreshold', 'kbScoreThreshold'],
    undefined
  );
  const conversionPrompt = resolveProp(
    props.conversionPrompt,
    ['conversionPrompt', 'agent.conversionPrompt'],
    ''
  );

  // Sessions
  const sessionHistoryEnabled = resolveProp(
    props.enableSessions,
    ['sessionHistory.enabled', 'enableSessions'],
    false
  );
  const maxSessions = resolveProp(
    props.maxSessions,
    ['sessionHistory.maxSessions', 'maxSessions'],
    50
  );

  // Features
  const allowAttachments = resolveProp(
    props.allowAttachments,
    ['features.allowAttachments', 'allowAttachments'],
    true
  );
  const allowVoice = resolveProp(props.allowVoice, ['features.allowVoice', 'allowVoice'], false);
  const allowVideo = resolveProp(props.allowVideo, ['features.allowVideo', 'allowVideo'], false);

  // Auth / base endpoints (needed by sub-hooks)
  const apiKey = resolveProp(props.apiKey, ['apiKey', 'auth.apiKey'], null);
  const apiBaseUrl = resolveProp(props.apiBaseUrl, ['endpoints.apiBaseUrl', 'apiBaseUrl'], null);

  // Streaming / features
  const provider = resolveProp(props.provider, ['provider'], undefined);
  const enableStreaming = resolveProp(
    props.enableStreaming,
    ['features.enableStreaming', 'enableStreaming'],
    false
  );
  const enableToolVisualization = resolveProp(
    props.enableToolVisualization,
    ['features.enableToolVisualization', 'enableToolVisualization'],
    true
  );
  const enableRichMessages = resolveProp(
    props.enableRichMessages,
    ['features.enableRichMessages', 'enableRichMessages'],
    true
  );
  const enableSourceCitations = resolveProp(
    props.enableSourceCitations,
    ['features.enableSourceCitations', 'enableSourceCitations'],
    false
  );
  const enableThinking = resolveProp(
    props.enableThinking,
    ['features.enableThinking', 'enableThinking'],
    true
  );

  // Open state
  const defaultOpen = resolveProp(props.defaultOpen, ['defaultOpen'], false);

  const filteredBubbleOptions = useMemo(() => {
    if (!Array.isArray(bubbleOptions)) return [];
    const blocked = new Set(['chat', 'chat bubble', 'chatbot', 'chat bot']);
    return bubbleOptions.filter(option => {
      const label = typeof option === 'string' ? option : option?.label;
      const value = typeof option === 'string' ? option : (option?.value ?? option?.label);
      const labelNorm = String(label || '')
        .trim()
        .toLowerCase();
      const valueNorm = String(value || '')
        .trim()
        .toLowerCase();
      return !(blocked.has(labelNorm) || blocked.has(valueNorm));
    });
  }, [bubbleOptions]);

  // Delegate to sub-hooks
  const endpoints = useEndpointConfig({ props, resolveProp, apiKey, apiBaseUrl });
  const behavior = useWidgetBehavior({ props, resolveProp, readSetting, sessionHistoryEnabled });

  return {
    settings,
    readSetting,
    companyName,
    companyLogo,
    agentName,
    agentAvatar,
    theme,
    darkMode,
    welcomeMessage,
    suggestedActions,
    bubbleOptions,
    placeholder,
    language,
    agentOrgId,
    agentId,
    kbCollection,
    kbApiKey,
    kbTopK,
    kbScoreThreshold,
    conversionPrompt,
    sessionHistoryEnabled,
    maxSessions,
    allowAttachments,
    allowVoice,
    allowVideo,
    apiKey,
    apiBaseUrl,
    provider,
    enableStreaming,
    enableToolVisualization,
    enableRichMessages,
    enableSourceCitations,
    enableThinking,
    defaultOpen,
    filteredBubbleOptions,
    ...endpoints,
    ...behavior,
  };
}
